import { Component, ViewChild, OnInit, Output, EventEmitter, Input, ChangeDetectionStrategy, OnChanges, SimpleChanges, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Grid as InavistaGrid, GridConfig, GridHyperlinkClickEvent, GridRowsSelectedEvent, GridToolbarActionClickEvent } from '@inavista/components/grid';
import { ApiResponse, OrderTypes } from 'projects/service/src/app/shared/models/app.model';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { statusPillColor } from 'projects/service/src/app/shared/utils/status-pill-colors';
import { HttpErrorResponse } from '@angular/common/http';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import {
  buildCycleCountOpenExceptionGridConfig, CREATE_ADJUSTMENT_ORDER_ACTION, CYCLE_COUNT_OPEN_EXCEPTION_COLUMNS,
} from './cycle-counts-open-exception.grid.config';

// The old grid's content was window.innerHeight - 420 tall, with its toolbar and header above it.
// The inavista grid draws them inside its own box.
const GRID_CHROME_PX = 92;

@Component({
    selector: 'app-cycle-counts-open-exception',
    templateUrl: './cycle-counts-open-exception.component.html',
    styleUrls: ['./cycle-counts-open-exception.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    // The grid reads this.openExceptionList, not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class CycleCountOpenExceptionComponent implements OnInit, OnChanges {
  showLoader: boolean = true;
  gridConfig: GridConfig = null;
  gridHeight: number = window.innerHeight - 420 + GRID_CHROME_PX;
  showPopup: boolean = false;
  adjustmentOrdersList = null
  showAdjustmentOrderListPopup: boolean = false;
  linkedPopupData: { id: string; type: string; } = null;
  // The ticked exceptions (the old getSelectedRecords()), in the order they were ticked.
  selectedExceptions: any[] = [];
  @Output() exceptionExist = new EventEmitter();
  @Output() exceptionGridExist = new EventEmitter();
  @Output() adjustmentOrderPopupClosed = new EventEmitter();
  @Input() openExceptionList: any = []

  @ViewChild('cycleCountOpenExceptionGrid') grid: InavistaGrid;

  USER: any;
  private readonly gridRows = inject(LocalRowsGridApi);
  private shownExceptions: any[] = [];

  constructor(
    private commonService: CommonService,
    private dialog: MatDialog,
  ) {
    this.USER = this.commonService.USER;
  }

  showMasterItemCodePopup(eqData: any) {
    if (!eqData?.itemCode) return;
    const dialogRef = this.dialog.open(MasterItemcodePopupComponent, {
      panelClass: 'master-itemcode-popup-dialog',
      width: '80%',
      height: '80%',
      data: eqData
    });
    dialogRef.afterClosed().subscribe((result) => { });
  }

  ngOnInit(): void {
    this.gridRows.columns.set(LocalRowsGridApi.schema(CYCLE_COUNT_OPEN_EXCEPTION_COLUMNS));
    // The old Create Adjustment Order button was greyed (and not clickable) until an exception was ticked.
    this.gridConfig = buildCycleCountOpenExceptionGridConfig(
      statusPillColor(s => this.commonService.getStatusClass(s)),
      () => this.selectedExceptions.length ? null : 'Tick at least one exception to create an adjustment order.');
    this.refreshRows();
    this.showLoader = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['openExceptionList'] && !changes['openExceptionList'].firstChange) {
      this.refreshRows();
    }
  }

  // The rows the grid shows: openExceptionList as it came, numbered. A new list clears the ticks, as the old grid did.
  refreshRows() {
    this.shownExceptions = Array.isArray(this.openExceptionList) ? this.openExceptionList : [];
    this.gridRows.rows.set(this.shownExceptions.map((exception, i) => ({ ...exception, __rowKey: i })));
    this.selectedExceptions = [];
    this.grid?.setCheckedKeys([], { emit: false });
    this.grid?.refresh();
  }

  private exceptionFor(row: any) {
    return this.shownExceptions[row?.['__rowKey'] as number] ?? row;
  }

  onRowsSelected(event: GridRowsSelectedEvent) {
    this.selectedExceptions = event.rows.map(row => this.exceptionFor(row));
  }

  // LN Shipment No opens the shipment order; Item and Description the item, as before.
  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    const exception = this.exceptionFor(event.row);
    if (event.columnId === 'shipmentOrderId') {
      this.openShipmentOrderPopup(exception.shipmentOrderId);
    } else {
      this.showMasterItemCodePopup(exception);
    }
  }

  onToolbarActionClick(event: GridToolbarActionClickEvent) {
    if (event.key === CREATE_ADJUSTMENT_ORDER_ACTION && this.selectedExceptions.length) {
      const selectedExceptions = [...this.selectedExceptions];
      this.commonService.showConfirmation(`Do you want to Approve all selected exceptions?`, 'Yes', 'No', 'Approve All Exceptions?')
        .then(result => {
          if (result.isConfirmed) {
            this.showLoader = true;
            this.approveAllExceptions(selectedExceptions)
          }
        });
      this.showPopup = true;
    }
  }

  // The old Excel export: the rows the grid shows, the visible columns, "Open Exceptions(<date>).xlsx".
  onExportRequested() {
    exportInavistaGrid(this.grid, 'Open Exceptions');
  }

  getStatusClass(status: string) {
    return this.commonService.getStatusClass(status);
  }

  openShipmentOrderPopup(shipmentOrderId) {
    this.linkedPopupData = {
      id: shipmentOrderId,
      type: OrderTypes.shipmentOrder
    };
  }

  approveAllExceptions(selectedExceptions) {
    this.commonService.callApi('ShipmentOrder/UpdateExceptionStatus?status=Adjusted', 'post', selectedExceptions)
      .subscribe((res: ApiResponse) => {
        if (res.isSuccessful) {
          this.commonService.callApi('ShipmentOrder/CreateAdjustmentOrder', 'post', selectedExceptions)
            .subscribe((response: ApiResponse) => {
              if (response.isSuccessful) {
                this.adjustmentOrdersList = response.result;

                setTimeout(() => {
                  this.showAdjustmentOrderListPopup = true;
                }, 500);
                this.commonService.showNotification('success', response.message || 'Status updated! successfully. Adjustment orders created');
              } else {
                this.commonService.showNotification('error', response.message || 'Something went wrong. Kindly, try again.');
                this.showLoader = false;
              }
            }, (error: HttpErrorResponse) => {
              this.showLoader = false;
            });
        } else {
          this.commonService.showNotification('error', res.message || 'Something went wrong. Kindly, try again.');
          this.showLoader = false;
        }
      }, (error: HttpErrorResponse) => {
        this.showLoader = false;
      });
  }

  adjustmentOrderListPopupClosed(event) {
    this.showLoader = false;
    this.showAdjustmentOrderListPopup = false;
    this.adjustmentOrdersList = [];
    this.adjustmentOrderPopupClosed.emit()
  }

  onClose() {
    this.showPopup = false;
  }

}
